import http from '@/http'
import {
  AutoMergeResult,
  Bounds,
  BomSaveRequest,
  BomViewResponse,
  BulkDuplicatePagesRequest,
  BulkDuplicatePagesResponse,
  BulkResetPagesRequest,
  BulkResetPagesResponse,
  BulkUpdateReviewStatusResponse,
  DuplicatePageResponse,
  IllustrationsSaveRequest,
  IllustrationsViewResponse,
  JobErrorsParams,
  JobErrorsResponse,
  MatchingBookResult,
  MatchingPageResult,
  MergePageRequest,
  MergePageResponse,
  PageDetailsInfoRequest,
  PageDetailsInfoResponse,
  PageIdsResponse,
  PageIdLookupResponse,
  PartLookupResponse,
  PdfImportCreateResponse,
  PdfImportOverviewParams,
  PdfImportOverviewResponse,
  PdfImportSearchParams,
  PdfImportSearchResult,
  PdfImportSubmission,
  PublishJobBookResultResponse,
  PublishJobListResponse,
  PublishJobPageResultsResponse,
  PublishJobResultFilter,
  PublishProgressResponse,
  PublishSelectedRequest,
  PublishSelectedResponse,
  ResetJobSubmittedResponse,
  ReviewStatus
} from '@/interfaces/admin/pdfImport'

// ============================================================================
// Local Interfaces
// ============================================================================

export interface SearchMatchingPageParams {
  pageFilename?: string | null
  translationName?: string | null
  locale: string
}

// ============================================================================
// PDF Import Controller
// ============================================================================

export class PdfImportController {
  private static readonly BASE_PATH = '/admin/pdf-imports'

  // --------------------------------------------------------------------------
  // Public Methods (Alphabetized)
  // --------------------------------------------------------------------------

  static async createPdfImport(submission: PdfImportSubmission): Promise<PdfImportCreateResponse> {
    const formData = new FormData()
    formData.append('file', submission.file)
    formData.append('request', JSON.stringify(submission.request))

    const response = await http.post<PdfImportCreateResponse>(`${this.BASE_PATH}/new`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  static async deletePdfImport(id: number): Promise<void> {
    await http.delete(`${this.BASE_PATH}/${id}`)
  }

  static async duplicatePage(importId: number, pageId: number): Promise<DuplicatePageResponse> {
    const response = await http.post<DuplicatePageResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/duplicate`
    )
    return response.data
  }

  static async duplicatePages(
    importId: number,
    request: BulkDuplicatePagesRequest
  ): Promise<BulkDuplicatePagesResponse> {
    const response = await http.post<BulkDuplicatePagesResponse>(
      `${this.BASE_PATH}/${importId}/pages/duplicate`,
      request
    )
    return response.data
  }

  static async getBom(importId: number, pageId: number): Promise<BomViewResponse> {
    const response = await http.get<BomViewResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/bom`
    )
    return response.data
  }

  static async getIllustrationHires(importId: number, illustrationId: number): Promise<Blob> {
    const response = await http.get<Blob>(
      `${this.BASE_PATH}/${importId}/illustrations/${illustrationId}/hires`,
      { responseType: 'blob' }
    )
    return response.data
  }

  static async getIllustrationPreview(
    importId: number,
    illustrationId: number,
    bounds: Bounds
  ): Promise<Blob> {
    const { left, top, height, width } = bounds
    const response = await http.get<Blob>(
      `${this.BASE_PATH}/${importId}/illustrations/${illustrationId}/preview`,
      { params: { left, top, height, width }, responseType: 'blob' }
    )
    return response.data
  }

  static async getIllustrations(
    importId: number,
    pageId: number
  ): Promise<IllustrationsViewResponse> {
    const response = await http.get<IllustrationsViewResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/illustrations`
    )
    return response.data
  }

  static async getPublishJobBook(
    importId: number,
    publishJobId: number
  ): Promise<PublishJobBookResultResponse> {
    const response = await http.get<PublishJobBookResultResponse>(
      `${this.BASE_PATH}/${importId}/publish-jobs/${publishJobId}/book`
    )
    return response.data
  }

  static async getPublishJobPages(
    importId: number,
    publishJobId: number,
    resultFilter?: PublishJobResultFilter,
    nameQuery?: string,
    pageNumberFilter?: string
  ): Promise<PublishJobPageResultsResponse> {
    const params: Record<string, any> = {}
    if (resultFilter && resultFilter !== PublishJobResultFilter.ALL) params.resultFilter = resultFilter
    if (nameQuery) params.nameQuery = nameQuery
    if (pageNumberFilter) params.pageNumberFilter = pageNumberFilter
    const response = await http.get<PublishJobPageResultsResponse>(
      `${this.BASE_PATH}/${importId}/publish-jobs/${publishJobId}/pages`,
      { params }
    )
    return response.data
  }

  static async getPublishJobs(
    importId: number,
    limit = 100,
    offset = 0
  ): Promise<PublishJobListResponse> {
    const response = await http.get<PublishJobListResponse>(
      `${this.BASE_PATH}/${importId}/publish-jobs`,
      { params: { limit, offset } }
    )
    return response.data
  }

  static async getLatestPublishSummary(importId: number): Promise<PublishProgressResponse> {
    const response = await http.get<PublishProgressResponse>(
      `${this.BASE_PATH}/${importId}/publish-jobs/latest/summary`
    )
    return response.data
  }

  static async getJobErrors(jobId: number, params?: JobErrorsParams): Promise<JobErrorsResponse> {
    const queryParams = this.buildJobErrorsQueryParams(params)
    const response = await http.get<JobErrorsResponse>(`${this.BASE_PATH}/${jobId}/errors`, { params: queryParams })
    return response.data
  }

  static async getOverview(id: number, params?: PdfImportOverviewParams): Promise<PdfImportOverviewResponse> {
    const queryParams = this.buildOverviewQueryParams(params)
    const response = await http.get<PdfImportOverviewResponse>(`${this.BASE_PATH}/${id}/overview`, { params: queryParams })
    return response.data
  }

  static async getPageByPosition(
    importId: number,
    position: number,
    includeDropped = false
  ): Promise<PageIdLookupResponse> {
    const response = await http.get<PageIdLookupResponse>(
      `${this.BASE_PATH}/${importId}/pages/by-position/${position}`,
      { params: { includeDropped: includeDropped || undefined } }
    )
    return response.data
  }

  static async getPageHires(importId: number, pageId: number): Promise<Blob> {
    const response = await http.get<Blob>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/hires`,
      { responseType: 'blob' }
    )
    return response.data
  }

  static async getPageIds(
    importId: number,
    params?: PdfImportOverviewParams
  ): Promise<PageIdsResponse> {
    const queryParams = this.buildOverviewQueryParams(params)
    const response = await http.get<PageIdsResponse>(
      `${this.BASE_PATH}/${importId}/pages/ids`,
      { params: queryParams }
    )
    return response.data
  }

  static async getPageInfo(
    importId: number,
    pageId: number,
    includeDropped = false
  ): Promise<PageDetailsInfoResponse> {
    const response = await http.get<PageDetailsInfoResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/info`,
      { params: { includeDropped: includeDropped || undefined } }
    )
    return response.data
  }

  static async getPagePreview(
    importId: number,
    pageId: number,
    bounds: Bounds
  ): Promise<Blob> {
    const { left, top, height, width } = bounds
    const response = await http.get<Blob>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/preview`,
      { params: { left, top, height, width }, responseType: 'blob' }
    )
    return response.data
  }

  static async autoMergePages(importId: number): Promise<AutoMergeResult> {
    const response = await http.post<AutoMergeResult>(
      `${this.BASE_PATH}/${importId}/pages/auto-merge`
    )
    return response.data
  }

  static async mergePages(
    importId: number,
    request: MergePageRequest
  ): Promise<MergePageResponse> {
    const response = await http.post<MergePageResponse>(
      `${this.BASE_PATH}/${importId}/pages/merge`,
      request
    )
    return response.data
  }

  static async publishSelected(
    importId: number,
    request: PublishSelectedRequest
  ): Promise<PublishSelectedResponse> {
    const response = await http.post<PublishSelectedResponse>(
      `${this.BASE_PATH}/${importId}/pages/publish-selected`,
      request
    )
    return response.data
  }

  static async resetPages(
    importId: number,
    request: BulkResetPagesRequest
  ): Promise<ResetJobSubmittedResponse> {
    const response = await http.post<ResetJobSubmittedResponse>(
      `${this.BASE_PATH}/${importId}/pages/reset`,
      request
    )
    return response.data
  }

  static async resetSinglePage(
    importId: number,
    pageId: number
  ): Promise<BulkResetPagesResponse> {
    const response = await http.post<BulkResetPagesResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/reset`
    )
    return response.data
  }


  static async saveBom(
    importId: number,
    pageId: number,
    request: BomSaveRequest
  ): Promise<BomViewResponse> {
    const response = await http.put<BomViewResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/bom`,
      request
    )
    return response.data
  }

  static async saveIllustrations(
    importId: number,
    pageId: number,
    request: IllustrationsSaveRequest
  ): Promise<IllustrationsViewResponse> {
    const response = await http.put<IllustrationsViewResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/illustrations`,
      request
    )
    return response.data
  }

  static async searchMatchingBook(identifier: string): Promise<MatchingBookResult> {
    const response = await http.get<MatchingBookResult>(
      `${this.BASE_PATH}/books/search-matching-book`,
      { params: { identifier } }
    )
    return response.data
  }

  static async searchMatchingPage(params: SearchMatchingPageParams): Promise<MatchingPageResult> {
    const queryParams = this.buildMatchingPageQueryParams(params)
    const response = await http.get<MatchingPageResult>(
      `${this.BASE_PATH}/pages/search-matching-page`,
      { params: queryParams }
    )
    return response.data
  }

  static async searchMatchingPart(
    importId: number,
    partNumber: string,
    supplierId: number,
    partName?: string | null
  ): Promise<PartLookupResponse> {
    const response = await http.get<PartLookupResponse>(
      `${this.BASE_PATH}/${importId}/bom/search-matching-part`,
      { params: { partNumber, supplierId, partName: partName || undefined } }
    )
    return response.data
  }

  static async searchPdfImports(params?: PdfImportSearchParams): Promise<PdfImportSearchResult> {
    const queryParams = this.buildQueryParams(params)
    const response = await http.get<PdfImportSearchResult>(this.BASE_PATH, { params: queryParams })
    return response.data
  }

  static async updatePageInfo(
    importId: number,
    pageId: number,
    request: PageDetailsInfoRequest
  ): Promise<PageDetailsInfoResponse> {
    const response = await http.put<PageDetailsInfoResponse>(
      `${this.BASE_PATH}/${importId}/pages/${pageId}/info`,
      request
    )
    return response.data
  }

  static async updatePageReviewStatus(
    importId: number,
    pageIds: number[],
    reviewStatus: ReviewStatus | null
  ): Promise<BulkUpdateReviewStatusResponse> {
    const response = await http.post<BulkUpdateReviewStatusResponse>(
      `${this.BASE_PATH}/${importId}/pages/review-status`,
      {
        pageIds,
        reviewStatus
      }
    )
    return response.data
  }

  // --------------------------------------------------------------------------
  // Private Helper Methods (Alphabetized)
  // --------------------------------------------------------------------------

  private static buildJobErrorsQueryParams(params?: JobErrorsParams): Record<string, any> {
    if (!params) return {}

    const queryParams: Record<string, any> = {}

    if (params.jobVersion !== undefined) queryParams.jobVersion = params.jobVersion
    if (params.phase) queryParams.phase = params.phase

    return queryParams
  }

  private static buildMatchingPageQueryParams(params: SearchMatchingPageParams): Record<string, any> {
    const queryParams: Record<string, any> = {
      locale: params.locale
    }

    if (params.pageFilename) queryParams.pageFilename = params.pageFilename
    if (params.translationName) queryParams.translationName = params.translationName

    return queryParams
  }

  private static buildOverviewQueryParams(params?: PdfImportOverviewParams): Record<string, any> {
    if (!params) return {}

    const queryParams: Record<string, any> = {}

    // Pagination
    if (params.limit !== undefined) queryParams.limit = params.limit
    if (params.offset !== undefined) queryParams.offset = params.offset

    // Search and text filters
    if (params.q) queryParams.q = params.q
    if (params.pageNumberFilter) queryParams.pageNumberFilter = params.pageNumberFilter

    // Multi-select filters
    if (params.reviewStatus) queryParams.reviewStatus = params.reviewStatus
    if (params.contentType) queryParams.contentType = params.contentType

    // Boolean flags
    if (params.incompleteBomOnly !== undefined) queryParams.incompleteBomOnly = params.incompleteBomOnly

    return queryParams
  }

  private static buildQueryParams(params?: PdfImportSearchParams): Record<string, any> {
    if (!params) return {}

    const queryParams: Record<string, any> = {}

    if (params.q) queryParams.q = params.q
    if (params.limit !== undefined) queryParams.limit = params.limit
    if (params.offset !== undefined) queryParams.offset = params.offset
    if (params.sort) queryParams.sort = params.sort
    if (params.order) queryParams.order = params.order
    if (params.createdAfter) queryParams.createdAfter = params.createdAfter
    if (params.createdBefore) queryParams.createdBefore = params.createdBefore
    if (params.updatedAfter) queryParams.updatedAfter = params.updatedAfter
    if (params.updatedBefore) queryParams.updatedBefore = params.updatedBefore
    if (params.status) queryParams.status = params.status

    return queryParams
  }
}

// ============================================================================
// Exported Function Bindings (Alphabetized)
// ============================================================================

export const autoMergePages = PdfImportController.autoMergePages.bind(PdfImportController)
export const createPdfImport = PdfImportController.createPdfImport.bind(PdfImportController)
export const deletePdfImport = PdfImportController.deletePdfImport.bind(PdfImportController)
export const duplicatePage = PdfImportController.duplicatePage.bind(PdfImportController)
export const duplicatePages = PdfImportController.duplicatePages.bind(PdfImportController)
export const getBom = PdfImportController.getBom.bind(PdfImportController)
export const getIllustrationHires = PdfImportController.getIllustrationHires.bind(PdfImportController)
export const getIllustrationPreview = PdfImportController.getIllustrationPreview.bind(PdfImportController)
export const getIllustrations = PdfImportController.getIllustrations.bind(PdfImportController)
export const getJobErrors = PdfImportController.getJobErrors.bind(PdfImportController)
export const getOverview = PdfImportController.getOverview.bind(PdfImportController)
export const getPageHires = PdfImportController.getPageHires.bind(PdfImportController)
export const getPageInfo = PdfImportController.getPageInfo.bind(PdfImportController)
export const getPagePreview = PdfImportController.getPagePreview.bind(PdfImportController)
export const getPublishJobBook = PdfImportController.getPublishJobBook.bind(PdfImportController)
export const getPublishJobPages = PdfImportController.getPublishJobPages.bind(PdfImportController)
export const getPublishJobs = PdfImportController.getPublishJobs.bind(PdfImportController)
export const mergePages = PdfImportController.mergePages.bind(PdfImportController)
export const publishSelected = PdfImportController.publishSelected.bind(PdfImportController)
export const resetPages = PdfImportController.resetPages.bind(PdfImportController)
export const resetSinglePage = PdfImportController.resetSinglePage.bind(PdfImportController)
export const saveBom = PdfImportController.saveBom.bind(PdfImportController)
export const saveIllustrations = PdfImportController.saveIllustrations.bind(PdfImportController)
export const searchMatchingBook = PdfImportController.searchMatchingBook.bind(PdfImportController)
export const searchMatchingPage = PdfImportController.searchMatchingPage.bind(PdfImportController)
export const searchMatchingPart = PdfImportController.searchMatchingPart.bind(PdfImportController)
export const searchPdfImports = PdfImportController.searchPdfImports.bind(PdfImportController)
export const updatePageInfo = PdfImportController.updatePageInfo.bind(PdfImportController)
export const updatePageReviewStatus = PdfImportController.updatePageReviewStatus.bind(PdfImportController)
